<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Integration from './Integration.vue';
import TelegramNotificationHelpText from './TelegramNotification/TelegramNotificationHelpText.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const botToken = ref('');
const chatId = ref('');

const integration = computed(() => {
  return store.getters['integrations/getIntegration']('telegram_notification');
});

const areHooksAvailable = computed(() => {
  const { hooks = [] } = integration.value || {};
  return !!hooks.length;
});

const uiFlags = computed(() => store.getters['integrations/getUIFlags']);

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return integration.value.action;
});

const connectTelegramNotification = async () => {
  if (!botToken.value || !chatId.value) return;

  try {
    await store.dispatch('integrations/connectTelegramNotification', {
      botToken: botToken.value,
      chatId: chatId.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.SUCCESS'));
    botToken.value = '';
    chatId.value = '';
  } catch (error) {
    useAlert(
      t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.CONNECT_ERROR')
    );
  }
};

const initializeIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeIntegration();
});
</script>

<template>
  <SettingsLayout
    :is-loading="!integrationLoaded || uiFlags.isCreatingTelegramNotification"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.HEADER')"
        description=""
        feature-name="telegram_notification_integration"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="space-y-5">
        <Integration
          :integration-id="integration.id"
          :integration-logo="integration.logo"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="integration.enabled"
          :integration-action="integrationAction"
          :action-button-text="
            $t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.DELETE')
          "
          :delete-confirmation-text="{
            title: $t(
              'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.DELETE_CONFIRMATION.TITLE'
            ),
            message: $t(
              'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.DELETE_CONFIRMATION.MESSAGE'
            ),
          }"
        >
          <template v-if="!areHooksAvailable" #action>
            <!-- Empty slot to prevent default connect link -->
            <span />
          </template>
        </Integration>

        <div
          v-if="!areHooksAvailable"
          class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h5 class="text-n-slate-12 text-heading-1">
            {{ $t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.TITLE') }}
          </h5>
          <Input
            v-model="botToken"
            type="text"
            :label="
              $t(
                'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.BOT_TOKEN_LABEL'
              )
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.BOT_TOKEN_PLACEHOLDER'
              )
            "
          />
          <Input
            v-model="chatId"
            type="text"
            :label="
              $t(
                'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.CHAT_ID_LABEL'
              )
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.CHAT_ID_PLACEHOLDER'
              )
            "
          />
          <div>
            <Button
              blue
              :label="
                $t('INTEGRATION_SETTINGS.TELEGRAM_NOTIFICATION.SETUP.CONNECT')
              "
              :disabled="!botToken || !chatId"
              @click="connectTelegramNotification"
            />
          </div>
        </div>

        <TelegramNotificationHelpText v-if="areHooksAvailable" />
      </div>
    </template>
  </SettingsLayout>
</template>
